import { colors as theme } from '../theme';
// Extracted verbatim from ClientManagement.jsx — shared by ClientManagement.jsx and
// all of its extracted tab components (ClientCheckInsTab, ClientNutritionTab,
// ClientPerformanceTab, ClientProgramsTab, ClientMessagesTab).

export const brandColors = {
  gold: theme.gold,
  darkGray: theme.bg,
  mediumGray: theme.surface1,
  lightGray: theme.surface3,
  silver: theme.textMuted,
  white: theme.text,
  red: theme.danger,
  green: theme.success,
  warning: theme.warning,
};

export const styles = {
  container: {
    padding: '24px',
    backgroundColor: brandColors.darkGray,
    minHeight: '100vh',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: brandColors.darkGray,
    color: brandColors.silver,
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: `3px solid ${brandColors.mediumGray}`,
    borderTop: `3px solid ${brandColors.gold}`,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    marginBottom: '16px',
  },
  // Back is a quiet, neutral button; gold is kept for the main action and the selected tab
  backBtn: {
    backgroundColor: 'transparent',
    border: `1px solid ${brandColors.lightGray}`,
    color: brandColors.white,
    padding: '10px 18px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
  },
  deleteClientBtn: {
    backgroundColor: 'transparent',
    border: `1px solid ${brandColors.red}`,
    color: brandColors.red,
    padding: '10px 16px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  equipmentBox: {
    backgroundColor: 'rgba(212, 175, 55, 0.1)',
    border: `1px solid ${brandColors.gold}`,
    borderRadius: '8px',
    padding: '12px',
    marginTop: '16px',
  },
  equipmentLabel: {
    color: brandColors.gold,
    fontSize: '13px',
    fontWeight: '600',
    marginBottom: '8px',
    display: 'block',
  },
  equipmentTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  equipmentTag: {
    backgroundColor: brandColors.lightGray,
    color: brandColors.white,
    padding: '4px 10px',
    borderRadius: '4px',
    fontSize: '12px',
    textTransform: 'capitalize',
  },
  title: {
    color: brandColors.white,
    fontSize: '28px',
    fontWeight: '700',
    marginBottom: '8px',
  },
  subtitle: {
    color: brandColors.silver,
    fontSize: '14px',
    marginBottom: '24px',
  },
  error: {
    backgroundColor: brandColors.red,
    color: brandColors.white,
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  clientsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '16px',
  },
  clientCard: {
    backgroundColor: brandColors.mediumGray,
    borderRadius: '14px',
    padding: '16px',
    cursor: 'pointer',
    transition: 'transform 0.2s, border-color 0.2s',
    border: `1px solid transparent`,
    position: 'relative',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  smallAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: brandColors.gold,
    color: brandColors.darkGray,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: '600',
  },
  cardInfo: {
    flex: 1,
  },
  cardName: {
    color: brandColors.white,
    fontSize: '16px',
    fontWeight: '600',
    margin: 0,
  },
  cardEmail: {
    color: brandColors.silver,
    fontSize: '12px',
    margin: 0,
  },
  cardDetails: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '12px',
  },
  tag: {
    backgroundColor: brandColors.darkGray,
    color: brandColors.silver,
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    textTransform: 'capitalize',
  },
  injuryTag: {
    backgroundColor: 'rgba(255, 69, 58, 0.2)',
    color: brandColors.red,
  },
  cardProgram: {
    borderTop: `1px solid ${brandColors.lightGray}`,
    paddingTop: '12px',
  },
  programTag: {
    color: brandColors.gold,
    fontSize: '13px',
    fontWeight: '500',
  },
  noProgramTag: {
    color: brandColors.silver,
    fontSize: '13px',
    fontStyle: 'italic',
  },

  // Client Detail View
  clientDetailCard: {
    backgroundColor: brandColors.mediumGray,
    borderRadius: '14px',
    padding: '24px',
    marginBottom: '24px',
  },
  clientHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '20px',
  },
  avatar: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: brandColors.gold,
    color: brandColors.darkGray,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '28px',
    fontWeight: '600',
  },
  clientName: {
    color: brandColors.white,
    fontSize: '24px',
    fontWeight: '600',
    margin: 0,
  },
  clientEmail: {
    color: brandColors.silver,
    fontSize: '14px',
    margin: 0,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: '16px',
    marginBottom: '16px',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  infoLabel: {
    color: brandColors.silver,
    fontSize: '12px',
  },
  infoValue: {
    color: brandColors.white,
    fontSize: '16px',
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  injuryBox: {
    backgroundColor: 'rgba(255, 69, 58, 0.1)',
    border: `1px solid ${brandColors.red}`,
    borderRadius: '8px',
    padding: '12px',
  },
  injuryLabel: {
    color: brandColors.red,
    fontSize: '13px',
    fontWeight: '600',
  },
  injuryText: {
    color: brandColors.white,
    fontSize: '14px',
    margin: '8px 0 0 0',
  },
  glp1Box: {
    backgroundColor: 'rgba(212, 175, 55, 0.08)',
    border: `1px solid ${brandColors.gold}`,
    borderRadius: '8px',
    padding: '12px',
    marginTop: '12px',
  },
  glp1Label: {
    color: brandColors.gold,
    fontSize: '13px',
    fontWeight: '600',
  },
  glp1Text: {
    color: brandColors.white,
    fontSize: '14px',
    margin: '8px 0 0 0',
    lineHeight: '1.5',
  },

  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    color: brandColors.gold,
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '16px',
  },
  programCard: {
    backgroundColor: brandColors.mediumGray,
    borderRadius: '14px',
    padding: '16px',
    marginBottom: '12px',
  },
  programHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
  },
  programName: {
    color: brandColors.white,
    fontSize: '16px',
    fontWeight: '600',
    margin: 0,
  },
  programDesc: {
    color: brandColors.silver,
    fontSize: '13px',
    margin: '4px 0 0 0',
  },
  programActions: {
    display: 'flex',
    gap: '8px',
  },
  viewBtn: {
    backgroundColor: brandColors.gold,
    color: brandColors.darkGray,
    border: 'none',
    padding: '8px 16px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  unassignBtn: {
    backgroundColor: 'transparent',
    border: `1px solid ${brandColors.red}`,
    color: brandColors.red,
    padding: '8px 16px',
    borderRadius: '8px',
    fontSize: '13px',
    cursor: 'pointer',
  },
  noProgram: {
    backgroundColor: brandColors.mediumGray,
    borderRadius: '14px',
    padding: '24px',
    textAlign: 'center',
    color: brandColors.silver,
  },
  assignRow: {
    display: 'flex',
    gap: '8px',
    marginTop: '16px',
    justifyContent: 'center',
  },
  select: {
    backgroundColor: brandColors.darkGray,
    color: brandColors.white,
    border: `1px solid ${brandColors.lightGray}`,
    padding: '10px',
    borderRadius: '8px',
    fontSize: '14px',
    minWidth: '200px',
  },
  assignBtn: {
    backgroundColor: brandColors.gold,
    color: brandColors.darkGray,
    border: 'none',
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  cancelBtn: {
    backgroundColor: 'transparent',
    border: `1px solid ${brandColors.lightGray}`,
    color: brandColors.silver,
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  addProgramBtn: {
    backgroundColor: 'transparent',
    border: `2px solid ${brandColors.gold}`,
    color: brandColors.gold,
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '16px',
  },

  // Program Editor
  programDetails: {
    marginTop: '20px',
    borderTop: `1px solid ${brandColors.lightGray}`,
    paddingTop: '20px',
  },
  weekBlock: {
    marginBottom: '20px',
  },
  weekTitle: {
    color: brandColors.gold,
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '12px',
  },
  dayBlock: {
    backgroundColor: brandColors.darkGray,
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '8px',
  },
  dayTitle: {
    color: brandColors.white,
    fontSize: '13px',
    fontWeight: '600',
    marginBottom: '10px',
  },
  exerciseList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  exerciseCard: {
    backgroundColor: brandColors.mediumGray,
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '8px',
  },
  exerciseRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  exerciseDropdown: {
    flex: 1,
    padding: '10px 12px',
    backgroundColor: brandColors.darkGray,
    border: `1px solid ${brandColors.lightGray}`,
    borderRadius: '8px',
    color: brandColors.white,
    fontSize: '14px',
    cursor: 'pointer',
    outline: 'none',
  },
  exerciseTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '10px',
  },
  exerciseInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  exerciseName: {
    color: brandColors.white,
    fontSize: '14px',
    fontWeight: '500',
  },
  muscleTag: {
    color: brandColors.gold,
    fontSize: '11px',
    textTransform: 'capitalize',
  },
  swapBtn: {
    backgroundColor: 'transparent',
    border: `1px solid ${brandColors.silver}`,
    color: brandColors.silver,
    padding: '4px 10px',
    borderRadius: '4px',
    fontSize: '11px',
    cursor: 'pointer',
  },
  swapPanel: {
    backgroundColor: brandColors.darkGray,
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '12px',
  },
  swapHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  swapTitle: {
    color: brandColors.silver,
    fontSize: '12px',
  },
  closeSwapBtn: {
    backgroundColor: 'transparent',
    border: 'none',
    color: brandColors.silver,
    fontSize: '16px',
    cursor: 'pointer',
    padding: '4px',
  },
  suggestionsList: {
    maxHeight: '200px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  suggestionItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 10px',
    backgroundColor: brandColors.mediumGray,
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  suggestionName: {
    color: brandColors.white,
    fontSize: '13px',
  },
  suggestionMuscle: {
    color: brandColors.silver,
    fontSize: '11px',
    textTransform: 'capitalize',
  },
  exerciseInputs: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  inputLabel: {
    color: brandColors.silver,
    fontSize: '11px',
  },
  smallInput: {
    width: '50px',
    padding: '8px',
    backgroundColor: brandColors.darkGray,
    border: `1px solid ${brandColors.lightGray}`,
    borderRadius: '4px',
    color: brandColors.white,
    fontSize: '14px',
    textAlign: 'center',
  },
  x: {
    color: brandColors.silver,
    fontSize: '14px',
    marginBottom: '8px',
  },
  closeEditBtn: {
    backgroundColor: brandColors.lightGray,
    color: brandColors.white,
    border: 'none',
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    marginTop: '16px',
  },

  // Photos
  photosGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: '12px',
  },
  photoCard: {
    position: 'relative',
    aspectRatio: '1',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: brandColors.mediumGray,
  },
  photo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  photoDate: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    backgroundColor: 'rgba(0,0,0,0.7)',
    color: brandColors.white,
    fontSize: '11px',
    padding: '4px',
    textAlign: 'center',
  },
  uploadCard: {
    aspectRatio: '1',
    borderRadius: '8px',
    border: `2px dashed ${brandColors.lightGray}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    backgroundColor: brandColors.mediumGray,
  },
  uploadIcon: {
    fontSize: '24px',
    color: brandColors.gold,
    marginBottom: '4px',
  },
  uploadText: {
    fontSize: '11px',
    color: brandColors.silver,
  },

  // Tabs
  tabContainer: {
    display: 'flex',
    flexWrap: 'wrap', // on a phone the five tabs wrap to two rows instead of running off the screen
    gap: '8px',
    marginBottom: '24px',
    borderBottom: `1px solid ${brandColors.lightGray}`,
    paddingBottom: '12px',
  },
  tabButton: {
    backgroundColor: 'transparent',
    border: `1px solid ${brandColors.lightGray}`,
    color: brandColors.silver,
    padding: '10px 16px',
    whiteSpace: 'nowrap',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  tabButtonActive: {
    backgroundColor: brandColors.gold,
    border: `1px solid ${brandColors.gold}`,
    color: brandColors.darkGray,
  },

  // Stats Grid
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    backgroundColor: brandColors.mediumGray,
    borderRadius: '14px',
    padding: '20px',
    textAlign: 'center',
    border: `1px solid ${brandColors.lightGray}`,
  },
  statValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: brandColors.white,
    marginBottom: '4px',
  },
  statLabel: {
    fontSize: '13px',
    color: brandColors.silver,
  },
  statSubtext: {
    fontSize: '11px',
    color: brandColors.silver,
    marginTop: '2px',
  },

  // Flags Section
  flagsSection: {
    backgroundColor: brandColors.mediumGray,
    borderRadius: '14px',
    padding: '16px',
    marginBottom: '24px',
    border: `1px solid ${brandColors.lightGray}`,
  },
  flagsTitle: {
    color: brandColors.white,
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '12px',
  },
  flagsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  flagBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '500',
    borderWidth: '1px',
    borderStyle: 'solid',
  },

  // Quick Summary
  quickSummary: {
    backgroundColor: brandColors.mediumGray,
    borderRadius: '14px',
    padding: '16px',
    border: `1px solid ${brandColors.lightGray}`,
  },
  quickSummaryTitle: {
    color: brandColors.silver,
    fontSize: '12px',
    fontWeight: '500',
    marginBottom: '6px',
  },
  quickSummaryText: {
    color: brandColors.gold,
    fontSize: '16px',
    fontWeight: '600',
  },

  // Sessions Tab
  sessionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  sessionCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: brandColors.mediumGray,
    borderRadius: '8px',
    padding: '16px',
    border: `1px solid ${brandColors.lightGray}`,
  },
  sessionInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  sessionDate: {
    color: brandColors.white,
    fontSize: '15px',
    fontWeight: '600',
  },
  sessionTime: {
    color: brandColors.silver,
    fontSize: '13px',
  },
  sessionStatus: {
    display: 'flex',
    alignItems: 'center',
  },
  completedBadge: {
    backgroundColor: 'rgba(50, 215, 75, 0.15)',
    color: brandColors.green,
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
};
